<script setup lang="ts">
const consentStore = useConsentStore()
const baseStore = useBaseStore()
const localePath = useLocalePath()

const advertisingChoice = ref(false)
const googleAnalyticsChoice = ref(false)
const isSaving = ref(false)

const isCustomizeScreen = computed(() => consentStore.preferencesScreen === 'customize')
const showSummaryPanel = computed(() => {
  return consentStore.preferencesOpen
    && !consentStore.automatedMode
    && !isCustomizeScreen.value
})
const isNativePlatform = computed(() => baseStore.platform === 'ios' || baseStore.platform === 'android')
const hasRegisteredModalOpen = ref(false)

watch(
  () => consentStore.preferencesOpen && isCustomizeScreen.value && isNativePlatform.value,
  (open) => {
    if (open && !hasRegisteredModalOpen.value) {
      baseStore.incrementModalOpen()
      hasRegisteredModalOpen.value = true
    }
    else if (!open && hasRegisteredModalOpen.value) {
      baseStore.decrementModalOpen()
      hasRegisteredModalOpen.value = false
    }
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  if (hasRegisteredModalOpen.value)
    baseStore.decrementModalOpen()
})

watch(
  () => consentStore.preferencesOpen,
  (open) => {
    if (open) {
      advertisingChoice.value = consentStore.hasDecision ? consentStore.advertising : false
      googleAnalyticsChoice.value = consentStore.hasDecision ? consentStore.googleAnalytics : false
    }
  },
  { immediate: true },
)

async function save(googleAnalytics: boolean, advertising: boolean): Promise<void> {
  isSaving.value = true
  try {
    await consentStore.saveConsent({ googleAnalytics, advertising })
  }
  finally {
    isSaving.value = false
  }
}

async function acceptAll(): Promise<void> {
  await save(true, true)
}

async function refuseAll(): Promise<void> {
  await save(false, false)
}
</script>

<template>
  <div
    v-if="showSummaryPanel"
    class="fixed inset-0 z-[59] bg-black/30 sm:hidden"
    aria-hidden="true"
  />

  <section
    v-if="showSummaryPanel"
    class="fixed inset-x-3 top-1/2 z-[60] max-h-[calc(80dvh-0.75rem)] -translate-y-1/2 overflow-y-auto rounded-xl border border-poivre-200 bg-white p-5 shadow-xl sm:inset-x-auto sm:top-auto sm:bottom-[calc(4.5rem+env(safe-area-inset-bottom))] sm:left-4 sm:w-[calc(100%-2rem)] sm:max-h-[calc(100dvh-6rem)] sm:max-w-xl sm:translate-y-0 xl:bottom-6 xl:left-61"
    role="dialog"
    aria-labelledby="consent-summary-title"
    aria-describedby="consent-summary-description"
  >
    <div class="space-y-4">
      <h2 id="consent-summary-title" class="text-lg font-semibold text-poivre-800">
        Nous respectons votre vie privée
      </h2>
      <p id="consent-summary-description" class="text-sm text-poivre-700">
        Avec votre accord, nous utilisons des témoins (cookies) pour améliorer votre expérience, personnaliser les contenus et les publicités et analyser notre trafic. Cliquez sur «Accepter» pour y consentir.
      </p>
      <p class="text-xs text-poivre-600">
        Vous pouvez modifier votre choix en tout temps. Consultez notre
        <ULink :to="localePath({ name: 'PrivacyPolicyPage' })" class="text-secondary-600 underline inline-flex">
          politique de confidentialité
        </ULink>.
      </p>
      <div class="grid gap-3 sm:grid-cols-2">
        <UButton
          block
          size="lg"
          color="secondary"
          :loading="isSaving"
          @click="acceptAll"
        >
          Accepter
        </UButton>
        <UButton
          block
          size="lg"
          color="secondary"
          variant="outline"
          :disabled="isSaving"
          @click="consentStore.openCustomize()"
        >
          Personnaliser
        </UButton>
      </div>
    </div>
  </section>

  <UModal
    :open="consentStore.preferencesOpen && isCustomizeScreen && !consentStore.automatedMode"
    :dismissible="false"
    :close="false"
    title="Personnaliser vos préférences"
    :ui="{ content: 'max-w-xl' }"
  >
    <template #title>
      <div class="flex w-full items-center justify-between gap-4">
        <span>Personnaliser vos préférences</span>
        <UButton
          color="neutral"
          variant="ghost"
          size="sm"
          icon="i-lucide-x"
          :disabled="isSaving"
          aria-label="Retour aux choix de consentement"
          @click="consentStore.openSummary()"
        />
      </div>
    </template>

    <template #body>
      <div class="space-y-5">
        <div class="rounded-lg border border-poivre-200 p-4">
          <div class="flex items-start justify-between gap-4">
            <div>
              <p class="font-semibold text-poivre-800">
                Fonctionnement essentiel
              </p>
              <p class="mt-1 text-sm text-poivre-600">
                Connexion, sécurité, préférences de l’application, mesure d’audience interne et soutien.
              </p>
            </div>
            <USwitch :model-value="true" color="secondary" disabled aria-label="Fonctionnement essentiel toujours actif" />
          </div>
        </div>

        <div class="rounded-lg border border-poivre-200 p-4">
          <div class="flex items-start justify-between gap-4">
            <div>
              <p class="font-semibold text-poivre-800">
                Mesure d’audience Google
              </p>
              <p class="mt-1 text-sm text-poivre-600">
                Google Analytics nous aide à comprendre l’utilisation de Glouton. Plausible demeure toujours actif comme mesure interne.
              </p>
            </div>
            <USwitch
              v-model="googleAnalyticsChoice"
              color="secondary"
              aria-label="Autoriser la mesure d’audience Google"
            />
          </div>
        </div>

        <div class="rounded-lg border border-poivre-200 p-4">
          <div class="flex items-start justify-between gap-4">
            <div>
              <p class="font-semibold text-poivre-800">
                Publicité et reciblage
              </p>
              <p class="mt-1 text-sm text-poivre-600">
                Facebook, Google et TikTok peuvent mesurer nos campagnes et personnaliser les publicités. Le pixel publicitaire M32 est également activé.
              </p>
            </div>
            <USwitch
              v-model="advertisingChoice"
              color="secondary"
              aria-label="Autoriser la publicité et le reciblage"
            />
          </div>
        </div>

        <p class="text-sm text-poivre-600">
          Les publicités peuvent continuer d’être affichées lorsque ce choix est désactivé, mais elles ne sont pas personnalisées par Glouton. À noter que les membres Extra profitent de Glouton sans publicités.
        </p>

        <div class="grid gap-3 sm:grid-cols-2">
          <UButton
            block
            size="lg"
            color="secondary"
            :loading="isSaving"
            @click="save(googleAnalyticsChoice, advertisingChoice)"
          >
            Enregistrer
          </UButton>
          <UButton
            block
            size="lg"
            color="secondary"
            variant="outline"
            :disabled="isSaving"
            @click="refuseAll"
          >
            Refuser tout
          </UButton>
        </div>

        <UButton
          variant="link"
          color="neutral"
          icon="i-lucide-arrow-left"
          :disabled="isSaving"
          class="px-0"
          :class="{ 'mb-14': isNativePlatform }"
          @click="consentStore.openSummary()"
        >
          Retour
        </UButton>
      </div>
    </template>
  </UModal>
</template>
